import React from "react";
import { Button, ButtonProps, CircularProgress, Box } from "@mui/material";

export interface StyledButtonProps extends Omit<ButtonProps, "variant"> {
  /** Button variant (default: contained with gradient) */
  variant?: "gradient" | "outlined" | "text" | "solid";
  /** Gradient colors (default: navy gradient) */
  gradient?: string;
  /** Whether button is in loading state */
  isLoading?: boolean;
  /** Loading text to display */
  loadingText?: string;
  /** Icon to display before text */
  startIcon?: React.ReactNode;
  /** Icon to display after text */
  endIcon?: React.ReactNode;
  /** Whether to show lift effect on hover (default: true for gradient) */
  showLiftEffect?: boolean;
}

/**
 * StyledButton Component
 *
 * Reusable button with consistent styling across the application.
 * Supports gradient backgrounds, loading states, and smooth animations.
 *
 * Features:
 * - Purple gradient background (default)
 * - Hover lift effect with enhanced shadow
 * - Loading state with spinner
 * - Custom gradients support
 * - Smooth transitions and animations
 *
 * @example
 * // Gradient button (default)
 * <StyledButton onClick={handleClick}>
 *   Submit
 * </StyledButton>
 *
 * @example
 * // Loading state
 * <StyledButton isLoading loadingText="Saving...">
 *   Save Changes
 * </StyledButton>
 *
 * @example
 * // Custom gradient
 * <StyledButton gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
 *   Custom Style
 * </StyledButton>
 *
 * @example
 * // Outlined variant
 * <StyledButton variant="outlined">
 *   Cancel
 * </StyledButton>
 */
const StyledButton: React.FC<StyledButtonProps> = ({
  variant = "gradient",
  gradient = "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
  isLoading = false,
  loadingText,
  startIcon,
  endIcon,
  showLiftEffect = true,
  children,
  disabled,
  sx,
  ...rest
}) => {
  // Determine if lift effect should be applied
  const shouldLift = showLiftEffect && variant === "gradient";

  // Build button styles based on variant
  const getVariantStyles = () => {
    switch (variant) {
      case "gradient":
        return {
          background: gradient,
          color: "white",
          boxShadow: "0 4px 16px rgba(26, 35, 126, 0.25)",
          fontWeight: 600,
          letterSpacing: "0.02em",
          "&:hover": {
            ...(shouldLift && {
              transform: "translateY(-2px)",
              boxShadow: "0 8px 28px rgba(26, 35, 126, 0.35)",
            }),
            background: gradient
              .replace("#1a237e", "#283593")
              .replace("#3949ab", "#5e6ac2"),
          },
          "&:active": {
            transform: "translateY(0px)",
          },
          "&:disabled": {
            background: "rgba(26, 35, 126, 0.3)",
            color: "rgba(255, 255, 255, 0.5)",
          },
        };

      case "outlined":
        return {
          borderColor: "primary.main",
          color: "primary.main",
          borderWidth: "2px",
          fontWeight: 600,
          "&:hover": {
            borderWidth: "2px",
            borderColor: "primary.dark",
            backgroundColor: "rgba(26, 35, 126, 0.08)",
            ...(shouldLift && {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 16px rgba(26, 35, 126, 0.15)",
            }),
          },
          "&:active": {
            transform: "translateY(0px)",
          },
        };

      case "text":
        return {
          color: "primary.main",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "rgba(26, 35, 126, 0.08)",
          },
        };

      case "solid":
        return {
          backgroundColor: "primary.main",
          color: "white",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "primary.dark",
            ...(shouldLift && {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 16px rgba(26, 35, 126, 0.25)",
            }),
          },
          "&:active": {
            transform: "translateY(0px)",
          },
        };

      default:
        return {};
    }
  };

  return (
    <Button
      disabled={disabled || isLoading}
      startIcon={!isLoading && startIcon}
      endIcon={!isLoading && endIcon}
      sx={{
        py: 1.5,
        borderRadius: 2,
        fontSize: "1rem",
        fontWeight: 600,
        textTransform: "none",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        ...getVariantStyles(),
        ...sx,
      }}
      {...rest}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={20} color="inherit" />
          <span>{loadingText || children}</span>
        </Box>
      ) : (
        children
      )}
    </Button>
  );
};

export default StyledButton;
